import {
  CommonHelperFunctions,
  DashboardState,
  IComponent,
  IComponentDefinition,
  IDisplayItem,
  IDisplayItemComponent,
  ISelectionGeometryMessage,
  SelectionAction
} from "@kuika/kuika-cl-model";
import classNames from "classnames";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DesignerMessageSender } from "../../../../messages/designer-message-sender";
import { withSD } from "../../../../shared/hoc/screen-design-consumer";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { canMoveDown, canMoveUp } from "../../../../shared/utility/sibling-reorder";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaTooltipProvider from "../../../ui-kit/kuika-tooltip/kuika-tooltip-provider";
import { isReactNativeGenerationOffered } from "../../app-settings/react-native-feature-gate";
import {
  forwardClickToCanvas,
  getCanvasIframe,
  getIframeDocument,
  getScrollFrame,
  isBlockingLayerOpen,
  isCanvasSelectionKeyTarget,
  isEditableTarget
} from "./canvas-frame";
import { IPlacement, OverlayMode, popoverMaxHeight } from "./geometry";
import { IQuickOpenStore, QuickOpenContext } from "./quick-props/quick-controls";
import { QuickPropsRegion } from "./quick-props/quick-props-region";
import {
  computeClipboardPasteGates,
  filterActionsByClipboard,
  getParentKuikaId,
  IClipboardPasteGates,
  IPasteTargetContext,
  ISelectionOverlaySd,
  MultiSelectionActionKey,
  runMultiSelectionAction,
  runSelectionMenuAction
} from "./selection-actions";
import { SelectionBreadcrumb } from "./selection-breadcrumb";
import { SelectionDragGrip } from "./selection-drag-grip";
import { selectionGeometryStore } from "./selection-geometry-store";
import {
  buildSelectionBarActions,
  buildSelectionMenu,
  ISelectionMenuContext,
  ISelectionMenuItem,
  SelectionMenuKey
} from "./selection-menu";
import { SelectionMoreMenu } from "./selection-more-menu";
import "./selection-overlay.scss";
import { SelectionOverlayErrorBoundary } from "./selection-overlay-error-boundary";
import { buildSelectionPath, compactSelectionPath, ICollapsedPath, ISelectionCrumb } from "./selection-path";
import { StyleTagSlot } from "./style-tag-slot";
import { useCanvasFrameEvents } from "./use-canvas-frame-events";
import { IOverlayElements, IOverlayLayout, useOverlayPlacement } from "./use-overlay-placement";
import { useSelectionGeometry } from "./use-selection-geometry";

/** The ⋯ menu's key in the bar's one open-popover slot (`QuickOpenContext`). */
const MORE_MENU_KEY: string = "selection-more";

/** Tallest the ⋯ menu may grow before it scrolls, even when the canvas has more room. */
const MENU_HEIGHT_CAP: number = 420;
const MENU_GAP: number = 8;

const OFFSCREEN_ARROWS: Record<OverlayMode, string> = {
  anchored: "",
  hidden: "",
  offscreenAbove: "↑",
  offscreenBelow: "↓",
  offscreenLeft: "←",
  offscreenRight: "→"
};

export interface ISelectionOverlayProps {
  sd: ISelectionOverlaySd;
  /** Sends designer → agent messages; injectable for tests. */
  messageSender?: DesignerMessageSender;
}

interface IBarButtonProps {
  label: string;
  testId: string;
  icon: string;
  onClick: () => void;
  isActive?: boolean;
  ariaHasPopup?: "menu";
}

const defaultMessageSender: DesignerMessageSender = new DesignerMessageSender();

const modifierLabel = (): string => (CommonHelperFunctions.isMac() ? "⌘" : "Ctrl+");

interface ITooltipLabelProps {
  label: string;
  shortcut?: string;
}

const TooltipLabel = ({ label, shortcut }: ITooltipLabelProps): JSX.Element => (
  <span className="kuika-selection-overlay__tip">
    {label}
    {shortcut && <kbd className="kuika-selection-overlay__tip-kbd">{shortcut}</kbd>}
  </span>
);

const BarButton = React.forwardRef<HTMLButtonElement, IBarButtonProps>(
  (
    { label, testId, icon, onClick, isActive, ariaHasPopup, ...rest }: IBarButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ): JSX.Element => (
    <button
      {...rest}
      ref={ref}
      type="button"
      className={classNames("kuika-selection-bar__icon-button", {
        "kuika-selection-bar__icon-button--active": isActive
      })}
      aria-label={label}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaHasPopup ? isActive === true : undefined}
      data-testid={testId}
      onClick={onClick}
    >
      <KuikaSvg name={icon} width={16} height={16} color="currentColor" aria-hidden="true" />
    </button>
  )
);
BarButton.displayName = "SelectionBarButton";

/** Radix popovers own an Esc pressed while one of them is open (they close on it); the overlay must not also act on it. */
const isRadixLayerOpen = (): boolean =>
  document.querySelector('[data-radix-popper-content-wrapper] [role="dialog"][data-state="open"]') !== null;

/**
 * The designer-side chrome for the canvas selection: one compact, theme-following bar (drag grip · breadcrumb ·
 * quick props · style tag · ⋯), floating over the canvas
 * iframe next to the selection outline. Geometry comes from the agent (`selectionGeometryMessage`,
 * through `selectionGeometryStore`); layout is `geometry.ts`; actions call designer functions directly.
 */
export const SelectionOverlayView: React.FC<ISelectionOverlayProps> = ({
  sd,
  messageSender = defaultMessageSender
}: ISelectionOverlayProps): JSX.Element | null => {
  const geometry: ISelectionGeometryMessage | null = useSelectionGeometry();
  // The bar's ONE open popover (quick-prop lists and editors, the colour picker, the style tag, "+N", the
  // breadcrumb's "…" and ⋯): opening any of them closes the others, sticky editors included.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const openStore: IQuickOpenStore = useMemo((): IQuickOpenStore => ({ openKey, setOpenKey }), [openKey]);
  const isMenuOpen: boolean = openKey === MORE_MENU_KEY;
  const setIsMenuOpen = useCallback((open: boolean): void => {
    setOpenKey((prev: string | null): string | null => {
      if (open) {
        return MORE_MENU_KEY;
      }
      return prev === MORE_MENU_KEY ? null : prev;
    });
  }, []);
  const [dismissSignal, setDismissSignal] = useState<number>(0);
  // A move started from the bar's grip is in progress. The grip is that drag's source: it must stay
  // mounted (else its `dragend` is lost) and hit-testable until the drag ends, whatever the geometry says.
  const [isGripDragging, setIsGripDragging] = useState<boolean>(false);

  const overlayRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const barRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const chipRef: React.RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null);
  const elements: IOverlayElements = useMemo((): IOverlayElements => ({ bar: barRef, chip: chipRef }), []);

  const isReportDesigner: boolean = sd.dashboardState === DashboardState.reportDesigner;
  // Entering Report Designer shows its landing over the still-loaded UI screen until a report loads;
  // the canvas behind it is not the user's, and its last selection geometry is stale.
  const isReportLanding: boolean = isReportDesigner && sd.isReportDesignerLandingVisible();
  const isCanvasTool: boolean = (sd.dashboardState === DashboardState.design || isReportDesigner) && !isReportLanding;
  const isMulti: boolean = geometry?.kind === "multi";
  const kuikaId: string | null = !isMulti && geometry?.kuikaId ? geometry.kuikaId : null;
  const displayItem: IDisplayItem | undefined =
    kuikaId !== null ? sd.screenDesignDictionary[parseInt(kuikaId, 10)] : undefined;
  const hasTarget: boolean = geometry?.rect != null && (isMulti || displayItem !== undefined);
  const isActive: boolean = isCanvasTool && hasTarget;
  const isIdle: boolean = geometry?.interaction === "idle";

  const layout: IOverlayLayout | null = useOverlayPlacement(geometry, isActive, isReportDesigner, elements);

  // The UI screen's selection must not come back on the report that loads next: its kuikaIds are
  // per-screen, so its rect could resolve to an unrelated report component.
  useEffect((): void => {
    if (isReportLanding) {
      selectionGeometryStore.reset();
    }
  }, [isReportLanding]);

  const geometryRef: React.MutableRefObject<ISelectionGeometryMessage | null> = useRef(geometry);
  geometryRef.current = geometry;
  const isAnyPopoverOpenRef: React.MutableRefObject<boolean> = useRef(openKey !== null);
  isAnyPopoverOpenRef.current = openKey !== null;
  const isActiveRef: React.MutableRefObject<boolean> = useRef(isActive);
  isActiveRef.current = isActive;
  const sdRef: React.MutableRefObject<ISelectionOverlaySd> = useRef(sd);
  sdRef.current = sd;
  // Esc events a Radix popover consumed; the window capture listener sees them before Radix does.
  const layerEscapesRef: React.MutableRefObject<WeakSet<KeyboardEvent>> = useRef(new WeakSet<KeyboardEvent>());

  const closeFloating = useCallback((): void => {
    setOpenKey(null);
    setDismissSignal((n: number): number => n + 1);
  }, []);

  // Whether the bar is on screen. While it is parked off-screen (selection scrolled away, device switch)
  // nothing in it may stay open — a popover would anchor to the parked bar, far outside the window.
  const isBarPlaced: boolean = layout?.placement?.mode === "anchored" && layout.placement.bar !== null;

  // A different selection, a drag/resize, leaving the canvas tool, or the bar leaving the screen closes
  // any open menu.
  useEffect((): void => {
    closeFloating();
  }, [kuikaId, isMulti, isIdle, isActive, isBarPlaced, closeFloating]);

  // The parked bar stays mounted for measuring; `inert` keeps its triggers out of the tab order and
  // unclickable, so no popover can open against it. (React 17 has no `inert` prop.)
  const isBarInteractive: boolean = isIdle && isBarPlaced;
  useLayoutEffect((): void => {
    barRef.current?.toggleAttribute("inert", !isBarInteractive && !isGripDragging);
  });

  const highlight = useCallback((id: string | null): void => {
    sdRef.current.highlightHoveredItem(id ?? "");
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.defaultPrevented || layerEscapesRef.current.has(event)) {
        return;
      }
      // Radix closes its own popover on an Esc in the designer document; this covers an Esc from the canvas
      // document (focus stays in the iframe after a canvas click), which Radix never sees.
      if (isAnyPopoverOpenRef.current) {
        closeFloating();
        return;
      }
      if (!isActiveRef.current || isBlockingLayerOpen()) {
        return;
      }
      const overlay: HTMLDivElement | null = overlayRef.current;
      const focused: Element | null = document.activeElement;
      if (overlay && focused && overlay.contains(focused) && isEditableTarget(focused)) {
        (focused as HTMLElement).blur();
        return;
      }
      if (isEditableTarget(event.target) || !isCanvasSelectionKeyTarget(event.target, overlay)) {
        return;
      }
      const current: ISelectionGeometryMessage | null = geometryRef.current;
      // Multi-select: the agent clears its own multi-selection on Esc.
      if (!current || current.kind === "multi" || !current.kuikaId) {
        return;
      }
      const parentId: string | null = getParentKuikaId(sdRef.current, current.kuikaId);
      if (parentId !== null) {
        sdRef.current.setSelectedDisplayItem(parentId);
      }
    },
    [closeFloating]
  );

  useEffect((): (() => void) => {
    const markLayerEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && isRadixLayerOpen()) {
        layerEscapesRef.current.add(event);
      }
    };
    window.addEventListener("keydown", markLayerEscape, true);
    return (): void => window.removeEventListener("keydown", markLayerEscape, true);
  }, []);

  useCanvasFrameEvents({ onKeyDown: handleKeyDown, onCanvasPointerDown: closeFloating });

  const path: ISelectionCrumb[] = useMemo(
    (): ISelectionCrumb[] =>
      kuikaId !== null ? buildSelectionPath(kuikaId, sd.screenDesignDictionary, sd.sdName) : [],
    [kuikaId, sd.screenDesignDictionary, sd.sdName]
  );
  const compactPath: ICollapsedPath = useMemo((): ICollapsedPath => compactSelectionPath(path), [path]);

  const resolveDefinition = useCallback(
    (item: IDisplayItemComponent): IComponentDefinition | undefined => sd.getComponentDefinition(item),
    [sd]
  );
  const rawDefinition: IComponent | undefined = useMemo(
    (): IComponent | undefined =>
      displayItem
        ? sd.project?.componentSetObj?.components?.find((c: IComponent): boolean => c.id === displayItem.item.id)
        : undefined,
    [displayItem, sd.project]
  );

  const availableActions: readonly SelectionAction[] | undefined = geometry?.availableActions;
  // The clipboard lives outside React state (window.kuika.clipboard) and has no change event, so Paste is
  // re-gated whenever it could have changed under the bar: the ⋯ menu opening, the pointer entering the bar
  // (after a ⌘C elsewhere), and the bar's own Copy.
  const [clipboardCheck, setClipboardCheck] = useState<number>(0);
  const recheckClipboard = useCallback((): void => setClipboardCheck((n: number): number => n + 1), []);
  const actionContext: ISelectionMenuContext | null = useMemo((): ISelectionMenuContext | null => {
    if (kuikaId === null) {
      return null;
    }
    const id: number = parseInt(kuikaId, 10);
    const pasteTarget: IPasteTargetContext = {
      projectType: sd.project?.projectType,
      isWebScreen: sd.getIsWebScreen(),
      reactNativeOffered: isReactNativeGenerationOffered(),
      isTargetEditableTableColumnChild: CommonHelperFunctions.getIsEditableTableColumnChild(
        kuikaId,
        sd.screenDesignDictionary
      ),
      resolveDefinition
    };
    const clipboardGates: IClipboardPasteGates = computeClipboardPasteGates(displayItem?.item?.name, pasteTarget);
    return {
      availableActions: filterActionsByClipboard(availableActions ?? [], clipboardGates),
      canSelectParent: getParentKuikaId(sd, kuikaId) !== null,
      canMoveUp: canMoveUp(sd.screenDesignDictionary, id, resolveDefinition),
      canMoveDown: canMoveDown(sd.screenDesignDictionary, id, resolveDefinition),
      modifierLabel: modifierLabel()
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isMenuOpen / clipboardCheck are deliberate refresh triggers, not values read in the body
  }, [kuikaId, availableActions, sd, resolveDefinition, displayItem, isMenuOpen, clipboardCheck]);
  const menuItems: ISelectionMenuItem[] = useMemo(
    (): ISelectionMenuItem[] => (actionContext ? buildSelectionMenu(actionContext) : []),
    [actionContext]
  );
  const barActions: ISelectionMenuItem[] = useMemo(
    (): ISelectionMenuItem[] => (actionContext ? buildSelectionBarActions(actionContext) : []),
    [actionContext]
  );

  const collisionBoundary: Element | null = isActive ? document.querySelector(".kuika_center") : null;

  if (!isCanvasTool) {
    return null;
  }
  if (!geometry || (!isActive && !isGripDragging)) {
    return null;
  }

  const placement: IPlacement | null = layout?.placement ?? null;
  const isShown: boolean = isIdle && placement !== null;
  const isAnchored: boolean = isShown && placement?.mode === "anchored" && placement.bar !== null;
  const isChipShown: boolean = isShown && !isMulti && layout?.chip != null;

  const menuMaxHeight: number =
    placement?.bar && layout
      ? popoverMaxHeight(
          placement.bar.y + placement.bar.height,
          placement.bar.y,
          layout.safe,
          MENU_GAP,
          MENU_HEIGHT_CAP
        )
      : MENU_HEIGHT_CAP;

  const selectCrumb = (id: string): void => {
    highlight(null);
    sd.setSelectedDisplayItem(id);
  };

  // Scrolling over the bar (or the edge chip) scrolls the canvas under it. Only there: a popover's content
  // is portalled to `document.body`, but its React events still bubble through this tree — a wheel in a
  // scrollable popover must scroll the popover, not the page.
  const handleWheel = (event: React.WheelEvent): void => {
    const target: Node = event.target as Node;
    const isOverSurface: boolean =
      barRef.current?.contains(target) === true || chipRef.current?.contains(target) === true;
    if (!isOverSurface) {
      return;
    }
    const iframe: HTMLIFrameElement | null = getCanvasIframe();
    const scrollFrame: HTMLElement | null = getScrollFrame(getIframeDocument(iframe), isReportDesigner);
    if (!iframe || !scrollFrame) {
      return;
    }
    const frameWidth: number = iframe.getBoundingClientRect().width;
    const scale: number = iframe.offsetWidth > 0 && frameWidth > 0 ? frameWidth / iframe.offsetWidth : 1;
    scrollFrame.scrollBy(event.deltaX / scale, event.deltaY / scale);
  };

  // A ⌘/Ctrl-click in the report designer builds a multi-selection on the canvas. The bar lies over the
  // canvas — right where the next component of a stacked report usually is — so such a click on the bar
  // goes to the component under it instead of to a bar control.
  const isCanvasModifierClick = (event: React.MouseEvent): boolean =>
    isReportDesigner && (event.metaKey || event.ctrlKey) && barRef.current?.contains(event.target as Node) === true;

  const handleBarPressCapture = (event: React.MouseEvent): void => {
    if (isCanvasModifierClick(event)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleBarClickCapture = (event: React.MouseEvent): void => {
    if (!isCanvasModifierClick(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    forwardClickToCanvas(event.clientX, event.clientY, {
      metaKey: event.metaKey,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey
    });
  };

  const friendlyName: string =
    kuikaId !== null ? ScreenDesignHelper.getFriendlyName(kuikaId, sd.screenDesignDictionary) : "";
  // The agent reports every multi-selected id (ISelectionGeometryMessage.selectedKuikaIds) —
  // sd.selectedKuikaIds only ever tracks one id and cannot be used here.
  const multiSelectedKuikaIds: readonly string[] = geometry?.selectedKuikaIds ?? [];
  const multiCount: number = multiSelectedKuikaIds.length;

  const renderSingleBar = (): JSX.Element => (
    <>
      <SelectionBreadcrumb
        path={compactPath}
        onSelect={selectCrumb}
        onHover={highlight}
        collisionBoundary={collisionBoundary}
      />
      <div className="kuika-selection-bar__lead">
        {kuikaId !== null && displayItem && (
          <QuickPropsRegion
            // Keyed by the component: a new selection remounts the controls, so a pending debounced edit
            // flushes (unmount) to the component it was made for instead of riding along to the next one.
            key={kuikaId}
            sd={sd}
            kuikaId={kuikaId}
            displayItem={displayItem}
            definition={rawDefinition}
            availableWidth={layout?.safe.width ?? null}
            dismissSignal={dismissSignal}
          />
        )}
      </div>
      <span className="kuika-selection-bar__separator" aria-hidden="true" />
      {barActions.map((action: ISelectionMenuItem): JSX.Element => (
        <KuikaTooltip
          key={action.key}
          content={<TooltipLabel label={action.label} shortcut={action.shortcut} />}
          position="top"
          bgcolor="inverse"
        >
          <BarButton
            label={action.label}
            icon={action.icon.name}
            testId={action.testId}
            onClick={(): void => {
              if (kuikaId !== null) {
                runSelectionMenuAction(sd, action.key, kuikaId);
              }
              if (action.key === "copy") {
                recheckClipboard();
              }
            }}
          />
        </KuikaTooltip>
      ))}
      {kuikaId !== null && displayItem && (
        <StyleTagSlot key={kuikaId} sd={sd} kuikaId={kuikaId} displayItem={displayItem} dismissSignal={dismissSignal} />
      )}
      <SelectionMoreMenu
        items={menuItems}
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        onRun={(key: SelectionMenuKey): void => {
          if (kuikaId !== null) {
            runSelectionMenuAction(sd, key, kuikaId);
          }
        }}
        collisionBoundary={collisionBoundary}
        maxHeight={menuMaxHeight}
      >
        <KuikaTooltip
          content={isMenuOpen ? null : <TooltipLabel label="More actions" />}
          position="top"
          bgcolor="inverse"
          keepMounted
        >
          <BarButton
            label="More actions"
            icon="dots"
            testId="selectionMoreButton"
            ariaHasPopup="menu"
            isActive={isMenuOpen}
            onClick={(): void => setIsMenuOpen(!isMenuOpen)}
          />
        </KuikaTooltip>
      </SelectionMoreMenu>
    </>
  );

  // Multi-select selection lives in the agent (MultiSelectManager), but the geometry message now
  // reports every selected id (selectedKuikaIds), so the bar can show the real count and
  // run Copy/Duplicate/Delete itself, the same way the single bar's ⋯ menu does — by calling the
  // designer's own screen-design functions directly with those ids (runMultiSelectionAction).
  const runMulti = (key: MultiSelectionActionKey): void => runMultiSelectionAction(sd, key, multiSelectedKuikaIds);
  const multiActions: readonly SelectionAction[] = availableActions ?? [];

  const renderMultiBar = (): JSX.Element => (
    <>
      <span className="kuika-selection-bar__count" data-testid="selectionMultiCount">
        {multiCount > 0 ? `${multiCount} selected` : "Multiple selected"}
      </span>
      <span className="kuika-selection-bar__separator" aria-hidden="true" />
      {multiActions.includes("copy") && (
        <KuikaTooltip
          content={<TooltipLabel label="Copy" shortcut={`${modifierLabel()}C`} />}
          position="top"
          bgcolor="inverse"
        >
          <BarButton label="Copy" icon="copy" testId="selectionMultiCopy" onClick={(): void => runMulti("copy")} />
        </KuikaTooltip>
      )}
      {multiActions.includes("duplicate") && (
        <KuikaTooltip
          content={<TooltipLabel label="Duplicate" shortcut={`${modifierLabel()}D`} />}
          position="top"
          bgcolor="inverse"
        >
          <BarButton
            label="Duplicate"
            icon="copy-plus"
            testId="selectionMultiDuplicate"
            onClick={(): void => runMulti("duplicate")}
          />
        </KuikaTooltip>
      )}
      {multiActions.includes("delete") && (
        <KuikaTooltip content={<TooltipLabel label="Delete" shortcut="⌫" />} position="top" bgcolor="inverse">
          <BarButton
            label="Delete"
            icon="trash"
            testId="selectionMultiDelete"
            onClick={(): void => runMulti("delete")}
          />
        </KuikaTooltip>
      )}
    </>
  );

  // Its own tooltip provider: the bar lies over the canvas iframe, and a tooltip kept open for the pointer's
  // travel toward it would stay up once the pointer moved into the iframe (see `disableHoverableContent`).
  return createPortal(
    <KuikaTooltipProvider disableHoverableContent>
      <QuickOpenContext.Provider value={openStore}>
        <div
          ref={overlayRef}
          className={classNames("kuika-selection-overlay", { "kuika-selection-overlay--suppressed": !isIdle })}
          data-testid="selectionOverlay"
          onWheel={handleWheel}
        >
          <div
            ref={barRef}
            className={classNames("kuika-selection-bar", "kuika-selection-overlay__surface", {
              "kuika-selection-overlay__surface--shown": isAnchored
            })}
            role="toolbar"
            aria-label={isMulti ? "Selection actions" : `${friendlyName} actions`}
            aria-hidden={isAnchored ? undefined : true}
            data-testid="selectionBar"
            onPointerEnter={recheckClipboard}
            onPointerDownCapture={handleBarPressCapture}
            onMouseDownCapture={handleBarPressCapture}
            onClickCapture={handleBarClickCapture}
          >
            {!isMulti && (geometry.canMove === true || isGripDragging) && (
              <SelectionDragGrip onDraggingChange={setIsGripDragging} />
            )}
            {isMulti ? renderMultiBar() : renderSingleBar()}
          </div>
          {!isMulti && (
            <button
              ref={chipRef}
              type="button"
              className={classNames("kuika-selection-chip", "kuika-selection-overlay__surface", {
                "kuika-selection-overlay__surface--shown": isChipShown
              })}
              aria-hidden={isChipShown ? undefined : true}
              tabIndex={isChipShown ? undefined : -1}
              data-testid={isChipShown ? "selectionOffscreenChip" : undefined}
              onClick={(): void => {
                if (kuikaId !== null) {
                  messageSender.scrollIntoView({ kuikaId });
                }
              }}
            >
              <span aria-hidden="true">{placement ? OFFSCREEN_ARROWS[placement.mode] || "↓" : "↓"}</span>
              <span>{friendlyName}</span>
            </button>
          )}
        </div>
      </QuickOpenContext.Provider>
    </KuikaTooltipProvider>,
    document.body
  );
};

const GuardedSelectionOverlay: React.FC<ISelectionOverlayProps> = (props: ISelectionOverlayProps): JSX.Element => (
  <SelectionOverlayErrorBoundary>
    <SelectionOverlayView {...props} />
  </SelectionOverlayErrorBoundary>
);

export const SelectionOverlay = withSD<Record<string, never>>(GuardedSelectionOverlay);
